/* MoviePalette custom search — replaces the Sparq app.
 *
 * WHY THIS EXISTS
 *   Sparq ships ~226 KB gzipped to every page and calls its own servers on every
 *   keystroke. The catalogue is 2,835 films of the shape "name (year)" — small enough
 *   to hold in the browser. So: fetch the index ONCE, lazily, on first focus of the
 *   search box, then match in memory with zero network round-trips.
 *
 * MATCHING
 *   Trigram overlap (Dice) + prefix/substring boost + bounded Levenshtein. Verified
 *   against real misspellings — twilgiht, intersteller, godfarther, the mtrix,
 *   avengrs, fight clb — all return the right film first.
 *
 * NO DEPENDENCIES. No jQuery. The page already loads three copies of that.
 */
(function () {
  'use strict';

  var INDEX_URL = window.MP_SEARCH_INDEX_URL;
  var CDN_BASE = window.MP_SEARCH_CDN_BASE || '';
  var MONEY = window.MP_SEARCH_CURRENCY || '$';
  var MAX = 8;

  var rows = null;      // see build_index.py for the row shape
  var payload = null;   // the whole parsed index, so the results page can read facets
  var base = '', dirs = ['products/', 'files/'];
  var norm = null;      // normalised names
  var grams = null;     // trigram sets
  var loading = null;

  /* ---------- text utils ---------- */

  function normalise(s) {
    return s.toLowerCase()
      .normalize('NFKD').replace(/[̀-ͯ]/g, '')  // strip accents: Amélie -> amelie
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9 ]+/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function trigrams(s) {
    var p = '  ' + s + ' ', set = Object.create(null);
    for (var i = 0; i < p.length - 2; i++) set[p.substr(i, 3)] = 1;
    return set;
  }

  function dice(a, b, aLen, bLen) {
    var shared = 0;
    for (var g in a) if (b[g]) shared++;
    return (aLen + bLen) ? (2 * shared) / (aLen + bLen) : 0;
  }

  function size(o) { var n = 0; for (var k in o) n++; return n; }

  /* Levenshtein, abandoned once it exceeds `cap` — we never care about far matches. */
  function lev(a, b, cap) {
    if (Math.abs(a.length - b.length) > cap) return cap + 1;
    var prev = [], cur = [], i, j;
    for (j = 0; j <= b.length; j++) prev[j] = j;
    for (i = 1; i <= a.length; i++) {
      cur[0] = i;
      var best = cur[0];
      for (j = 1; j <= b.length; j++) {
        cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1,
                          prev[j - 1] + (a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1));
        if (cur[j] < best) best = cur[j];
      }
      if (best > cap) return cap + 1;
      var t = prev; prev = cur; cur = t;
    }
    return prev[b.length];
  }

  /* ---------- index ---------- */

  function load() {
    if (loading) return loading;
    loading = fetch(INDEX_URL, { credentials: 'omit' })
      .then(function (r) { return r.json(); })
      .then(function (d) {
        payload = d;
        rows = d.rows;
        base = d.base || '';
        if (d.dirs) dirs = d.dirs;
        norm = new Array(rows.length);
        grams = new Array(rows.length);
        for (var i = 0; i < rows.length; i++) {
          var n = normalise(rows[i][0]);
          norm[i] = n;
          var g = trigrams(n);
          grams[i] = { g: g, len: size(g) };
        }
        return true;
      })
      .catch(function (e) { loading = null; throw e; });
    return loading;
  }

  /* ---------- search ---------- */

  function search(q, limit) {
    var qn = normalise(q);
    if (!qn || !rows) return [];
    var qg = trigrams(qn), qlen = size(qg), out = [], i;

    for (i = 0; i < rows.length; i++) {
      var n = norm[i], s = 0;
      var at = n.indexOf(qn);
      if (at === 0) s = 1.35;                       // starts with the query
      else if (at > 0) s = 1.05;                    // contains it
      else if (n.indexOf(' ' + qn) > -1) s = 1.1;   // matches a later word
      else {
        s = dice(qg, grams[i].g, qlen, grams[i].len);
        if (s < 0.2) continue;                      // cheap reject before Levenshtein
        var d = lev(qn, n.substr(0, qn.length + 3), 3);
        if (d <= 1) s += 0.5; else if (d <= 2) s += 0.35; else if (d <= 3) s += 0.15;
      }
      if (s > 0.3) out.push([s, i]);
    }

    out.sort(function (a, b) {
      if (b[0] !== a[0]) return b[0] - a[0];
      return rows[b[1]][1] - rows[a[1]][1];         // tie-break: newer film first
    });
    return out.slice(0, limit || MAX).map(function (o) { return rows[o[1]]; });
  }

  /* ---------- ui ---------- */

  /* Use the ?width= query parameter, NOT the _500x filename suffix. The suffix form
     404s for most files in the CDN's files/ folder — verified: 404 on the hashed
     names, 200 on a handful of others. The query form works for both folders. */
  function img(file, dir, w) {
    if (!file) return '';
    return base + (dirs[dir] || dirs[0]) + file + '?width=' + w;
  }

  /* The Admin API reports the wrong CDN folder for some products, so retry the other
     one once before giving up. Same fix as the results page. */
  window.mpsImgError = function (el) {
    if (el.dataset.retried) { el.style.visibility = 'hidden'; return; }
    el.dataset.retried = '1';
    el.src = el.dataset.alt;
  };

  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function render(box, results, q) {
    if (!results.length) {
      box.innerHTML = '<div class="mps-empty">No films matching &ldquo;' + esc(q) +
        '&rdquo;<span>Try the title on its own, e.g. &ldquo;dune&rdquo;</span></div>';
      box.hidden = false;
      return;
    }
    var h = '<ul class="mps-list" role="listbox">';
    for (var i = 0; i < results.length; i++) {
      var r = results[i];
      h += '<li role="option" aria-selected="false"><a href="/products/' + esc(r[2]) + '">' +
        // no loading="lazy": these are built inside a hidden box, so the browser
        // never fires the lazy load and they stay blank. 8 thumbs at 80px is nothing.
        (r[3] ? '<img src="' + esc(img(r[3], r[6], 80)) +
          '" data-alt="' + esc(img(r[3], r[6] ? 0 : 1, 80)) + '" onerror="mpsImgError(this)"' +
          ' alt="" width="40" height="40">' : '<span class="mps-noimg"></span>') +
        '<span class="mps-t"><strong>' + esc(r[0]) + '</strong>' +
        '<small>' + (r[1] || '') + (r[5] && r[5].length ? ' &middot; ' + esc(r[5][0]) : '') + '</small></span>' +
        '<span class="mps-p">' + MONEY + r[4] + '</span></a></li>';
    }
    h += '</ul><a class="mps-all" href="/search?q=' + encodeURIComponent(q) + '">See all results for &ldquo;' + esc(q) + '&rdquo;</a>';
    box.innerHTML = h;
    box.hidden = false;
  }

  function attach(input) {
    if (input.dataset.mpsBound) return;
    input.dataset.mpsBound = '1';
    input.setAttribute('autocomplete', 'off');

    var box = document.createElement('div');
    box.className = 'mps-box';
    box.hidden = true;
    (input.closest('form') || input.parentNode).appendChild(box);

    var t = null, cursor = -1;

    function close() { box.hidden = true; cursor = -1; }

    function run() {
      var q = input.value.trim();
      if (q.length < 2) { close(); return; }
      load().then(function () { render(box, search(q), q); }).catch(function () { close(); });
    }

    input.addEventListener('focus', function () { load(); });   // warm the index early
    input.addEventListener('input', function () {
      clearTimeout(t);
      t = setTimeout(run, 60);
    });

    input.addEventListener('keydown', function (e) {
      var items = box.querySelectorAll('li a');
      if (!items.length || box.hidden) return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        cursor += (e.key === 'ArrowDown' ? 1 : -1);
        if (cursor < 0) cursor = items.length - 1;
        if (cursor >= items.length) cursor = 0;
        for (var i = 0; i < items.length; i++) items[i].classList.toggle('mps-on', i === cursor);
        items[cursor].scrollIntoView({ block: 'nearest' });
      } else if (e.key === 'Enter' && cursor > -1) {
        e.preventDefault();
        window.location.href = items[cursor].getAttribute('href');
      } else if (e.key === 'Escape') {
        close();
      }
    });

    document.addEventListener('click', function (e) {
      if (!box.contains(e.target) && e.target !== input) close();
    });
  }

  function init() {
    // Only the product-search fields. The theme's country selector is also an
    // input[type=search], so match on name="q" and exclude the localisation form.
    var sel = 'input[name="q"], .search__input, #Search-In-Modal';
    document.querySelectorAll(sel).forEach(function (el) {
      if (el.name === 'country_filter') return;
      if (el.closest('.country-filter, localization-form, .disclosure')) return;
      attach(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // header/search often mounts late (drawers, modals) — catch those too
  new MutationObserver(init).observe(document.documentElement, { childList: true, subtree: true });

  window.MPSearch = {
    search: search,
    load: load,
    data: function () { return payload; }
  };
})();
